import { FC, useState } from 'react';
import { Box, Divider, IconButton, Popover, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { Assessment, OverallResults } from '@libs/types';
import { ChartPieSliceIcon, InfoIcon } from '@phosphor-icons/react';

import { OverallResultsViewModel } from '../../types';

// ── Ring layout ───────────────────────────────────────────────────────────────

const RING_CONFIGS: Record<1 | 2, { r: number; stroke: number }[]> = {
  1: [{ r: 85, stroke: 22 }],
  2: [{ r: 88, stroke: 16 }, { r: 62, stroke: 14 }],
};

const SVG_SIZE = 200;
const CX = SVG_SIZE / 2;
const CY = SVG_SIZE / 2;

// ── Types ─────────────────────────────────────────────────────────────────────

interface RingData {
  score: number;
  color: string;
  label: string;
}

export interface DomainPieChartProps {
  overallResults: OverallResultsViewModel;
  comparisonOverall?: OverallResults;
  activeVersion?: Assessment;
  baselineVersion?: Assessment;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const DomainPieChart: FC<DomainPieChartProps> = observer(
  ({ overallResults, comparisonOverall, activeVersion, baselineVersion }) => {
    const theme = useTheme();
    const { t, i18n } = useTrans();
    const { isRtl } = useDirection();
    const lang = i18n.language === 'ar' ? 'ar' : 'en';

    const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

    const trackColor   = theme.palette.grey[100];
    const hasScore     = typeof overallResults.maturityScore === 'number';
    const currentScore = Math.max(0, Math.min(100, Math.round(overallResults.maturityScore ?? 0)));
    const currentColor = overallResults.maturityConfig?.color?.hex ?? theme.palette.primary.main;
    const currentLevel = overallResults.maturityConfig?.label?.[lang] ?? `Level ${overallResults.maturityLevel}`;

    const labels = {
      title:     lang === 'ar' ? 'نظرة عامة على النضج'                 : 'Maturity Overview',
      subtitle:  lang === 'ar' ? 'درجة النضج الإجمالية'                : 'Overall maturity score',
      howToRead: lang === 'ar' ? 'كيفية قراءة هذا الرسم'               : 'How to read this chart',
      versions:  t('assessments.overview.charts.versions') || (lang === 'ar' ? 'الإصدارات' : 'Versions'),
    };

    const infoItems = lang === 'ar'
      ? [
          { label: 'درجة الحلقة',   desc: 'القوس المعبأ يُظهر نسبة جاهزيتك الإجمالية. حلقة كاملة = 100%.' },
          { label: 'الرقم المركزي', desc: 'درجة النضج الإجمالية عبر جميع النطاقات المُقيَّمة.' },
          { label: 'مستوى النضج',   desc: 'الشارة تُظهر مرحلة النضج (مثل: ابتدائي، مُدار، مُحسَّن).' },
          { label: 'الحلقة الثانية', desc: 'عند المقارنة بين الإصدارات تظهر حلقة ثانية تُمثّل النتيجة الأساسية.' },
        ]
      : [
          { label: 'Ring score',    desc: 'The filled arc shows your overall readiness. A full circle = 100% ready.' },
          { label: 'Center number', desc: 'Your current overall maturity score across all assessed domains.' },
          { label: 'Maturity level', desc: 'The badge shows your maturity tier (e.g. Initial, Managed, Optimized).' },
          { label: 'Second ring',   desc: 'When comparing versions a second ring shows the baseline score.' },
        ];

    // ── Empty state ───────────────────────────────────────────────────────────
    if (!hasScore) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
              mb: 0.5,
            }}
          >
            <ChartPieSliceIcon size={22} />
          </Box>
          <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
            {t('assessments.overview.charts.empty.title')}
          </Typography>
          <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', maxWidth: 240 }}>
            {t('assessments.overview.charts.empty.subtitle')}
          </Typography>
        </Box>
      );
    }

    // ── Build ring data ───────────────────────────────────────────────────────
    const hasBaseline = !!comparisonOverall && !!baselineVersion;

    const rings: RingData[] = [
      {
        score: currentScore,
        color: currentColor,
        label: t('assessments.overview.charts.versionCurrent', { version: activeVersion?.version ?? '' }),
      },
    ];

    if (hasBaseline) {
      const bScore = Math.max(0, Math.min(100, Math.round(comparisonOverall!.maturity?.percentage ?? 0)));
      rings.push({
        score: bScore,
        color: theme.palette.grey[500],
        label: t('assessments.overview.charts.versionBaseline', { version: baselineVersion!.version }),
      });
    }

    const ringCount = Math.min(rings.length, 2) as 1 | 2;
    const configs   = RING_CONFIGS[ringCount];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* ── Header ── */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.25 }}>
              {labels.title}
            </Typography>
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', mt: 0.375, lineHeight: 1.35, fontSize: '0.7rem' }}>
              {labels.subtitle}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => setInfoAnchor(e.currentTarget)}
            sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' }, mt: -0.25 }}
          >
            <InfoIcon size={16} />
          </IconButton>
        </Box>

        {/* ── Info popover ── */}
        <Popover
          open={Boolean(infoAnchor)}
          anchorEl={infoAnchor}
          onClose={() => setInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
          slotProps={{
            paper: {
              sx: {
                maxWidth: 280,
                p: 2,
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${theme.palette.grey[200]}`,
              },
            },
          }}
        >
          <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 1 }}>
            {labels.howToRead}
          </Typography>
          <Divider sx={{ mb: 1.25, borderColor: 'grey.100' }} />
          {infoItems.map(({ label, desc }) => (
            <Box key={label} sx={{ mb: 1.25 }}>
              <Typography variant="textXS_semiBold" sx={{ color: 'text.primary', display: 'block' }}>{label}</Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>{desc}</Typography>
            </Box>
          ))}
        </Popover>

        {/* ── Content ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            minHeight: 0,
          }}
        >
          {/* Donut rings */}
          <Box sx={{ position: 'relative', width: '100%', maxWidth: SVG_SIZE, height: SVG_SIZE, flexShrink: 0, mt: 1 }}>
            <svg width="100%" height={SVG_SIZE} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}>
              {configs.map(({ r, stroke }, idx) => {
                const ring   = rings[idx];
                if (!ring) return null;
                const c      = 2 * Math.PI * r;
                const filled = (ring.score / 100) * c;

                return (
                  <g key={idx}>
                    <circle cx={CX} cy={CY} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
                    {ring.score > 0 && (
                      <circle
                        cx={CX} cy={CY} r={r}
                        fill="none"
                        stroke={ring.color}
                        strokeWidth={stroke}
                        strokeLinecap={filled > stroke * 1.5 ? 'round' : 'butt'}
                        strokeDasharray={`${filled} ${c - filled}`}
                        strokeDashoffset={c / 4}
                        style={{ transition: 'stroke-dasharray 0.5s ease, stroke 0.3s ease' }}
                      />
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Center label */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1, color: currentColor }}>
                {currentScore}%
              </Typography>
              {activeVersion && (
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                  V{activeVersion.version}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Maturity level badge */}
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: `${currentColor}14`,
              border: `1px solid ${currentColor}40`,
              mt: 2,
            }}
          >
            <Typography variant="textSM_semiBold" sx={{ color: currentColor }}>
              {currentLevel}
            </Typography>
          </Box>

          {/* Ring legend */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%', mt: 2 }}>
            <Typography
              variant="textXS_semiBold"
              sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem', mb: 0.5 }}
            >
              {labels.versions}
            </Typography>
            {rings.map((ring, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: ring.color, flexShrink: 0 }} />
                <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
                  {ring.label}: <strong>{ring.score}%</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  },
);
